// ----------------------------------------------------------------------
// <copyright file="Izenda.UserAccountDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  const { join, map, split, chain, isEmpty } = require('lodash/fp');

  var m = require('mobx');
  var routeUtils = require('share/utils/Izenda.RouteUtils');
  var trackable = require('share/tracking/Izenda.Trackable');
  var constants = require('logic/data/Izenda.UserAccountConstants');
  const config = require('config');

  function UserAccountDetail(data) {
    var props = {
      id: null,
      systemAdmin: false,
      userName: null,
      password: null,
      tenantId: null,
      emailAddress: null,
      roles: [],
      state: null,
      inserted: null,
      version: null,
      created: null,
      createdBy: null,
      modified: null,
      modifiedBy: null,
      selected: false,
      lastName: null,
      firstName: null,
      fullName: null,
      active: false,
      initPassword: false,
      passwordHash: null,
      passwordSalt: null,
      currentTokenHash: null,
      deleted: false,
      userSecurityQuestions: null,
      userRoles: null,
      dataOffset: 0,
      timestampOffset: 0,
      passwordLink: null,
      failedlogin: false,
      status: null,
      isNew: function() {
        return !this.id;
      },
      inactiveReason: function() {
        return config.Integration && !this.active
          ? constants.INACTIVE_REASON.INTEGRATION
          : constants.INACTIVE_REASON[this.status];
      },
      rolesValue: null,
      recipientValue: [],
      clearSercurityQuestion: false,
      sendEmail: false,
      cultureName: 'en-US',
      dateFormat: 'MM/DD/YYYY',
      validateResultRoleAssociation: [],
      formHasErrors: false, // form has errors or not
      hasChangeLanguage: false
    };

    m.extendObservable(trackable(this), props);
    data && this.populateData(data);
  }

  /**
   * Populate data
   * @param  {Object} user account detail data
   */
  UserAccountDetail.prototype.populateData = m.action(function(data) {
    data = data || {};
    this.id = data.id;
    this.systemAdmin = data.systemAdmin;
    this.userName = data.userName;
    this.tenantId = data.tenantId;
    this.emailAddress = data.emailAddress;
    this.roles = data.roles;
    this.state = data.state;
    this.inserted = data.inserted;
    this.version = data.version;
    this.created = data.created;
    this.createdBy = data.createdBy;
    this.modified = data.modified;
    this.modifiedBy = data.modifiedBy;
    this.lastName = data.lastName;
    this.firstName = data.firstName;
    this.fullName = data.fullName;
    this.active = data.active;
    this.password = data.password;
    this.passwordHash = data.passwordHash;
    this.passwordSalt = data.passwordSalt;
    this.currentTokenHash = data.currentTokenHash;
    this.deleted = data.deleted;
    this.userSecurityQuestions = data.userSecurityQuestions;
    this.userRoles = data.userRoles;
    this.dataOffset = data.dataOffset;
    this.timestampOffset = data.timestampOffset;
    this.initPassword = data.initPassword;
    this.status = data.status;
    this.cultureName = data.cultureName;
    this.dateFormat = data.dateFormat;
    this.selected = data.selected;

    // populate roles
    this.populateRole();
    this.hasChangeLanguage = false;
  });

  /**
   * Update selected user account
   * @param  {boolean} value
   */
  UserAccountDetail.prototype.updateSelected = function(value) {
    this.selected = value;
    this.markDirty();
  };

  /**
   * Check user account dirty or not
   * @return {boolean} result of dirty
   */
  UserAccountDetail.prototype.checkDirty = function() {
    return this.isDirty;
  };
  /**
   * populate Password Link
   * @param  {string} password
   */
  UserAccountDetail.prototype.populatePasswordLink = function(result) {
    if (result.success) {
      var password = result.data;
      var encodePassworkLink = encodeURIComponent(password);
      var urlConfig = routeUtils.getRouteUrl(routeUtils.getConfigRoute('Account', 'Activation'));
      if (urlConfig) {
        this.passwordLink = routeUtils.makeAbsoluteUrl(urlConfig, 'verification=' + encodePassworkLink);
      }
    }

    return result;
  };

  /**
   * update User Active status
   * @param  {boolean} state
   */
  UserAccountDetail.prototype.updateUserActive = function(state) {
    this.active = state;
  };

  /**
   * update User name
   * @param  {string} username
   */
  UserAccountDetail.prototype.updateUserName = function(userName) {
    this.userName = userName;
    this.markDirty();
  };

  /**
   * update First Name
   * @param  {string} firstName
   */
  UserAccountDetail.prototype.updateFirstName = function(firstName) {
    this.firstName = firstName;
    this.markDirty();
  };

  /**
   * update last Name
   * @param  {string} lastName
   */
  UserAccountDetail.prototype.updateLastName = function(lastName) {
    this.lastName = lastName;
    this.markDirty();
  };

  /**
   * update Email Address
   * @param  {string} email
   */
  UserAccountDetail.prototype.updateEmail = function(email) {
    this.emailAddress = email;
    this.markDirty();
  };

  /**
   * add Email Recipient into Password options pop up
   */
  UserAccountDetail.prototype.addEmailRecipient = function() {
    this.emailAddress && this.recipientValue.push({ emailAddress: this.emailAddress });
  };

  /**
   * update Role
   * @param  {string} roles
   */
  UserAccountDetail.prototype.populateRole = function() {
    this.rolesValue = join(',', map('id', this.roles));
  };

  /**
   * update Role
   * @param  {string} roles
   */
  UserAccountDetail.prototype.updateRole = function(roles) {
    this.rolesValue = roles;
    this.markDirty();
  };

  /**
   * update Timezone DataOffset
   * @param  {string} dataOffset
   */
  UserAccountDetail.prototype.updateTimezoneDataOffset = function(dataOffset) {
    this.dataOffset = dataOffset;
    this.markDirty();
  };

  /**
   * update Timezone Timestamp Offset
   * @param  {string} timestampOffset
   */
  UserAccountDetail.prototype.updateTimezoneTimestampOffset = function(timestampOffset) {
    this.timestampOffset = timestampOffset;
    this.markDirty();
  };

  /**
   * get Data For Save
   * @return {object}
   */
  UserAccountDetail.prototype.getDataForSave = m.action(function(configUser) {
    var listRoleSelected = split(',', configUser.rolesValue);
    var userRoles = [];
    listRoleSelected.forEach(function(roleIdItem) {
      userRoles.push({ id: roleIdItem });
    });
    var data = {
      id: configUser.id,
      systemAdmin: configUser.systemAdmin,
      userName: configUser.userName,
      tenantId: configUser.tenantId,
      emailAddress: configUser.emailAddress,
      roles: !configUser.systemAdmin ? userRoles : [],
      state: configUser.state,
      inserted: configUser.inserted,
      version: configUser.version,
      created: configUser.created,
      createdBy: configUser.createdBy,
      modified: configUser.modified,
      modifiedBy: configUser.modifiedBy,
      lastName: configUser.lastName,
      firstName: configUser.firstName,
      fullName: configUser.fullName,
      active: configUser.active,
      password: configUser.password,
      passwordHash: configUser.passwordHash,
      passwordSalt: configUser.passwordSalt,
      currentTokenHash: configUser.currentTokenHash,
      deleted: configUser.deleted,
      userSecurityQuestions: configUser.userSecurityQuestions,
      userRoles: configUser.userRoles,
      dataOffset: configUser.dataOffset,
      timestampOffset: configUser.timestampOffset,
      initPassword: configUser.initPassword,
      status: configUser.status,
      cultureName: configUser.cultureName,
      dateFormat: configUser.dateFormat
    };

    return data;
  });

  /**
   * add Recipients
   * @param {array} userAccountList
   */
  UserAccountDetail.prototype.addRecipients = m.action(function(userAccountList) {
    this.resetRecipientValue();
    this.recipientValue.push(
      chain(userAccountList)
        .flatMap(function(userAccount) {
          return userAccount.emailAddress;
        })
        .join(';')
        .value()
    );

    isEmpty(this.recipientValue[0]) && this.resetRecipientValue();
  });

  /**
   * get Recipients
   * @return {array} recipientList
   */
  UserAccountDetail.prototype.getRecipients = m.action(function(userAccountList) {
    this.resetRecipientValue();
    userAccountList.forEach(
      function(item) {
        item.selected && !isEmpty(item.emailAddress) && this.recipientValue.push({ emailAddress: item.emailAddress });
      }.bind(this)
    );
  });

  /**
   * reset Rcipient Value
   * @return  empty array
   */
  UserAccountDetail.prototype.resetRecipientValue = function() {
    this.recipientValue = [];
  };

  /**
   * reset password link Value
   * @return null value
   */
  UserAccountDetail.prototype.resetPasswordLink = function() {
    this.passwordLink = null;
  };

  /**
   * update Send Email State
   * @param  {boolean} state
   */
  UserAccountDetail.prototype.updateSendEmailState = function(state) {
    this.sendEmail = state;
  };

  /**
   * get Data For Save Password Options
   * @return {object}
   */
  UserAccountDetail.prototype.getDataForSavePasswordOptions = function() {
    var emailAddress = [];
    this.recipientValue &&
      this.recipientValue.forEach(function(item) {
        emailAddress.push(item.emailAddress);
      });
    var data = {
      passwordLink: this.passwordLink,
      user: {
        userName: this.userName,
        id: this.id
      },
      sendEmail: this.sendEmail,
      clearSercurityQuestion: false,
      emailAddresses: emailAddress
    };

    return data;
  };

  /**
   * update Language
   * @param  {string} language
   */
  UserAccountDetail.prototype.updateLanguage = m.action(function(language) {
    this.cultureName = language.value;
    this.hasChangeLanguage = true;
    this.markDirty();
  });

  /**
   * update date format
   * @param  {string} dateFormat
   */
  UserAccountDetail.prototype.updateDateFormat = m.action(function(dateFormat) {
    this.dateFormat = dateFormat.value;
    this.markDirty();
  });

  /**
   * reset Value Time Zone
   * @param  {string} type
   */
  UserAccountDetail.prototype.resetValueTimeZone = m.action(function(type) {
    this[type] = 0;
    this.markDirty();
  });

  /**
   * update formHasErrors
   * @param {Boolean} hasErrors
   */
  UserAccountDetail.prototype.updateFormHasErrors = function(hasErrors) {
    this.formHasErrors = hasErrors;
  };

  /**
   * update Selected System Admin
   * @param  {boolean} value
   */
  UserAccountDetail.prototype.updateSelectedSystemAdmin = m.action(function(value) {
    this.systemAdmin = value;
    this.markDirty();
  });

  /**
   * populate data to show in pop up after validate Role Association
   * @param  {[type]} value [description]
   */
  UserAccountDetail.prototype.populateAfterValidateRoleAssociation = m.action(function(data) {
    this.validateResultRoleAssociation = data;
    this.markDirty();
  });

  module.exports = UserAccountDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.UserAccountDetail.js